import { useMutation } from "@tanstack/react-query"
import moment, { Moment } from "moment"
import { useContext, useEffect, useRef, useState } from "react"
import api from "~/api"
import { EditReservationContext } from "~/contexts/admin/EditReservationContext"
import { AdminContext } from "~/contexts/AdminContext"
import { useReservation } from "../../ReservationHooks/useReservation"
import { ReservationProps } from "~/interfaces"

type Props = {
  setIsLoading: (signal: boolean) => void
  startDate: Moment
  endDate: Moment
}

export const useSummary = (props: Props) => {
  const {
    siteId,
    setSiteId,
    campingStyle,
    site,
    selectedBookingIntervalId,
    setSelectedBookingIntervalId,
    updateLocalReservation,
  } = useContext(EditReservationContext)
  const { camp, selectedReservation, campingStyles } = useContext(AdminContext)
  const {
    index: { data: reservationResp, refetch: refetchReservation },
  } = useReservation({ reservationId: selectedReservation?.id })
  const reservation = reservationResp?.reservation || selectedReservation

  const [isFocusStart, setIsFocusStart] = useState(false)
  const [isFocusEnd, setIsFocusEnd] = useState(false)
  const [additionalNotesValue, setAdditionalNotesValue] = useState(reservation?.notes)
  const isHourly = !!campingStyles.find((cs) => cs.id === site?.camping_style_id)?.default_hours
  const startDateValue = moment(reservation?.deleted_at).isValid() ? reservation?.start_date : props.startDate
  const endDateValue = moment(reservation?.deleted_at).isValid() ? reservation?.end_date : props.endDate

  const { mutate: updateReservationCheckedInStatusMutation, isPending: isMutatingUpdateReservation } = useMutation({
    mutationFn: async ({ isCheckedIn, isCheckedOut }: { isCheckedIn: boolean; isCheckedOut: boolean }) => {
      try {
        return await api.updateReservation(reservation?.id || "", {
          is_checked_in: isCheckedIn,
          is_checked_out: isCheckedOut,
        })
      } catch (error) {
        console.error("Error updating reservation:", error)
        throw error
      }
    },
    onSuccess: (data: ReservationProps) => {
      updateLocalReservation(data)
    },
    onSettled: () => {
      refetchReservation()
    },
  })

  useEffect(() => {
    if (isMutatingUpdateReservation) props.setIsLoading(true)
    props.setIsLoading(false)
  }, [isMutatingUpdateReservation])

  const handleClickCheckedIn = (e: any) => {
    let isCheckedIn: boolean = false
    let isCheckedOut: boolean = false

    if (!reservation?.is_checked_in && !reservation?.is_checked_out) {
      isCheckedIn = true
      isCheckedOut = false
    } else if (reservation?.is_checked_in && !reservation?.is_checked_out) {
      isCheckedIn = true
      isCheckedOut = true
    } else if (reservation?.is_checked_in && reservation?.is_checked_out) {
      isCheckedIn = false
      isCheckedOut = false
    }

    props.setIsLoading(true)
    updateReservationCheckedInStatusMutation({ isCheckedIn, isCheckedOut })
  }

  return {
    handleClickCheckedIn,
    isMutatingUpdateReservation,
    siteId,
    setSiteId,
    campingStyle,
    isHourly,
    camp,
    selectedReservation,
    startDateValue,
    endDateValue,
    selectedBookingIntervalId,
    setSelectedBookingIntervalId,
    additionalNotesValue,
    setAdditionalNotesValue,
    isFocusStart,
    setIsFocusStart,
    isFocusEnd,
    setIsFocusEnd,
  }
}
